/* eslint-disable no-undef */
/**
 * Data layer event system
 *
 * This module provides a clean interface for communicating with the data layer
 * without components needing to know about the underlying services.
 *
 * Events:
 * - db:refresh - Fetch fresh data from the server for all entities
 * - db:sync - Push all pending local changes to the server
 * - db:refresh:clients - Refresh clients from server
 * - db:refresh:templates - Refresh document templates from server
 * - db:refresh:documents - Refresh documents from server
 * - db:refresh:client-documents - Refresh documents for a specific client
 * - db:refresh:notes - Refresh notes for a specific client
 * - db:sync:notes - Sync pending notes
 * - db:sync:documents - Sync pending documents
 */

// Event name constants
export const DataEvents = {
  // Global events
  REFRESH: 'db:refresh',
  SYNC: 'db:sync',

  // Refresh events (pull from server)
  REFRESH_CLIENTS: 'db:refresh:clients',
  REFRESH_TEMPLATES: 'db:refresh:templates',
  REFRESH_DOCUMENTS: 'db:refresh:documents',
  REFRESH_CLIENT_DOCUMENTS: 'db:refresh:client-documents',
  REFRESH_PROGRAMS: 'db:refresh:programs',
  REFRESH_SITES: 'db:refresh:sites',

  // Sync events (push to server)
  SYNC_DOCUMENTS: 'db:sync:documents',
  SYNC_DOCUMENT: 'db:sync:document',
  SYNC_ANALYTICS: 'db:sync:analytics',

  // Pull events (fetch single resource from server)
  PULL_DOCUMENT: 'db:pull:document',
} as const;

export type DataEventName = (typeof DataEvents)[keyof typeof DataEvents];

/**
 * Resolved value of a debounced dispatch: the handler did not run because an
 * identical event ran within its debounce window. Callers that must not lose
 * work (the reconnect handler) check for it and fall back to a cheaper event.
 */
export const EVENT_DEBOUNCED: unique symbol = Symbol('event-debounced');

/** Events registered with a debounce window; their dispatch may resolve with EVENT_DEBOUNCED. */
export type DebouncedEventName =
  | typeof DataEvents.REFRESH
  | typeof DataEvents.SYNC
  | typeof DataEvents.REFRESH_CLIENTS
  | typeof DataEvents.REFRESH_TEMPLATES
  | typeof DataEvents.REFRESH_DOCUMENTS
  | typeof DataEvents.REFRESH_PROGRAMS
  | typeof DataEvents.REFRESH_SITES
  | typeof DataEvents.SYNC_ANALYTICS;

/** Resolved type of dispatchDataEvent: debounced events may also resolve with EVENT_DEBOUNCED. */
export type DispatchResult<T extends DataEventName, R> = T extends DebouncedEventName ? R | typeof EVENT_DEBOUNCED : R;

/**
 * Window events (not data events) that ask the data layer to do a full refresh
 * because the app just became able to sync: the encrypted database was
 * unlocked, or a signed-in session became available at startup.
 */
export const AppEvents = {
  UNLOCKED: 'app-unlocked',
  SESSION_READY: 'app-session-ready',
} as const;

// Event detail types
/**
 * Why a sync was requested. Progress and completion are only shown to the
 * user for a reconnect or a manual request (`force`); background refreshes
 * (dashboard load, startup, unlock, tab mounts) run silently. Failures are
 * always shown.
 */
export type SyncReason = 'reconnect';

export interface RefreshDetail {
  /** Skip the debounce window — a user asked for this sync (sidebar, Settings). */
  force?: boolean;
  /**
   * Forget every validator stored for the current organization before pulling,
   * so every page is fetched in full whatever the server would have said to
   * the stored ETags. Settings > "Sync now" only: it is support's escape hatch
   * for data that looks out of date, and it costs a full pull.
   */
  ignoreCache?: boolean;
  reason?: SyncReason;
}

export interface SyncDetail {
  reason?: SyncReason;
}

export interface RefreshClientDocumentsDetail {
  clientId: number;
}

/** `force` re-sends documents the sweep is holding — an explicit user gesture. */
export interface SyncDocumentsDetail {
  force?: boolean;
}

export interface SyncDocumentDetail {
  documentId: number | string;
  /** Re-send a payload the server already rejected. Only for a retry the user
   *  asked for — an automatic caller must leave this unset so the document
   *  keeps holding (see isSyncBlocked). */
  force?: boolean;
}

export interface PullDocumentDetail {
  clientId: number;
  documentId: number;
}

// Type-safe event detail mapping
export interface DataEventDetailMap {
  [DataEvents.REFRESH]: RefreshDetail | undefined;
  [DataEvents.SYNC]: SyncDetail | undefined;
  [DataEvents.REFRESH_CLIENTS]: undefined;
  [DataEvents.REFRESH_TEMPLATES]: undefined;
  [DataEvents.REFRESH_DOCUMENTS]: undefined;
  [DataEvents.REFRESH_CLIENT_DOCUMENTS]: RefreshClientDocumentsDetail;
  [DataEvents.REFRESH_PROGRAMS]: undefined;
  [DataEvents.REFRESH_SITES]: undefined;
  [DataEvents.SYNC_DOCUMENTS]: SyncDocumentsDetail | undefined;
  [DataEvents.SYNC_DOCUMENT]: SyncDocumentDetail;
  [DataEvents.SYNC_ANALYTICS]: undefined;
  [DataEvents.PULL_DOCUMENT]: PullDocumentDetail;
}

// Internal: Promise resolution functions for pending events
interface PendingEvent {
  resolve: (value?: unknown) => void;
  reject: (error: Error) => void;
}

const pendingEvents = new Map<string, PendingEvent>();
let eventIdCounter = 0;

/**
 * Dispatch a data layer event and optionally wait for completion
 *
 * @example
 * // Fire and forget
 * dispatchDataEvent('db:refresh');
 *
 * // Wait for completion
 * await dispatchDataEvent('db:sync');
 *
 * // Sync a specific document and handle errors
 * try {
 *   const updatedDocument = await dispatchDataEvent('db:sync:document', { documentId: 123 });
 *   console.log('Synced document:', updatedDocument);
 * } catch (error) {
 *   console.error('Sync failed:', error);
 * }
 */
export function dispatchDataEvent<T extends DataEventName, R = void>(
  eventName: T,
  detail?: DataEventDetailMap[T]
): Promise<DispatchResult<T, R>> {
  const eventId = `${eventName}-${++eventIdCounter}`;

  return new Promise<DispatchResult<T, R>>((resolve, reject) => {
    pendingEvents.set(eventId, { resolve: resolve as (value?: unknown) => void, reject });

    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: { ...detail, __eventId: eventId }
      })
    );
  });
}

/**
 * Resolve a pending event (called by event handlers)
 * @param eventId The event ID to resolve
 * @param value Optional value to resolve the promise with
 */
export function resolveDataEvent<T = void>(eventId: string, value?: T): void {
  const pending = pendingEvents.get(eventId);
  if (pending) {
    pending.resolve(value);
    pendingEvents.delete(eventId);
  }
}

/**
 * Reject a pending event (called by event handlers)
 */
export function rejectDataEvent(eventId: string, error: Error): void {
  const pending = pendingEvents.get(eventId);
  if (pending) {
    pending.reject(error);
    pendingEvents.delete(eventId);
  }
}

// Extended detail type that includes the internal event ID
export type EventDetailWithId<T> = T & { __eventId: string };

/**
 * Type-safe event listener helper
 */
export function addDataEventListener<T extends DataEventName>(
  eventName: T,
  handler: (detail: EventDetailWithId<DataEventDetailMap[T]>) => void | Promise<void>
): () => void {
  const listener = (event: Event) => {
    const customEvent = event as CustomEvent<EventDetailWithId<DataEventDetailMap[T]>>;
    handler(customEvent.detail);
  };

  document.addEventListener(eventName, listener);

  // Return cleanup function
  return () => document.removeEventListener(eventName, listener);
}
